import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { OrgStatBadge } from '#/components/OrgStatBadge/OrgStatBadge';
import { BodyMicro } from '#/components/Text/BodyMicro';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SearchAlgoliaOrgHit } from '#/modules/search/algolia/types/SearchAlgoliaOrgHit';
import { SearchHitImage } from '#/modules/search/components/Hit/Image/SearchHitImage';
import { SearchHitFloatingSaveButton } from '#/modules/search/components/Hit/SaveButton/SearchHitFloatingSaveButton';
import {
  SearchHitCard,
  SearchHitHolder,
  SearchHitHoverEffect,
  SearchHitImageHolder,
} from '#/modules/search/components/Hit/SearchHit.styled';
import { SearchHitInteractiveContentWrapper } from '#/modules/search/components/Hit/SearchHitInteractiveContentWrapper';
import type { SearchContextHeaderLocationState } from '#/modules/search/containers/SearchContextHeader/SearchContextHeaderLocationState';
import { getSearchHitElementId } from '#/modules/search/utils/getSearchHitElementId';
import type { SearchSavedListingsStore } from '#/modules/search/zustand-stores/searchSavedListingsStore';
import type { DatetimeString } from '#/types/TypedDateString';
import { timeago } from '#/utils/string/datetime/timeago';

import { SearchGroupHitInfo } from './SearchGroupHitInfo';

type Props = {
  hit: SearchAlgoliaOrgHit;
  href: string;
  trackClick: (() => void) | null;
  showLabel: boolean;
  searchSavedListingsStore: SearchSavedListingsStore | null;
  highlighted: boolean;
  now: DatetimeString;
};

export function SearchGroupHit({
  hit,
  href,
  trackClick,
  showLabel,
  searchSavedListingsStore,
  highlighted,
  now,
}: Props) {
  const { i18n, locale } = useI18n();

  const orgStat = useMemo(() => {
    if (hit.hiring) {
      return (
        <OrgStatBadge iconName="star-check" text={i18n('Actively Hiring')} />
      );
    }
    if (hit.hasUpcomingEvents) {
      return (
        <OrgStatBadge iconName="calendar" text={i18n('Upcoming Events')} />
      );
    }
    if (hit.seekingVolunteers) {
      return (
        <OrgStatBadge iconName="group" text={i18n('Seeking Volunteers')} />
      );
    }
    return null;
  }, [i18n, hit]);

  const listingStats = useMemo(() => {
    const stats: ReactNode[] = [];

    if (hit.published) {
      stats.push(
        <BodyMicro lineHeight={1}>
          {i18n('Posted %(postedAt)s', {
            postedAt: timeago(new Date(hit.published * 1000).toISOString(), {
              locale,
              now,
            }),
          })}
        </BodyMicro>,
      );
    }

    return stats;
  }, [i18n, locale, hit, now]);

  return (
    <SearchHitHolder
      id={getSearchHitElementId(hit.objectID)}
      data-qa-id="search-result"
    >
      <SearchHitCard>
        <SearchHitInteractiveContentWrapper
          highlighted={Boolean(highlighted)}
          listingLinkData={{
            type: 'link-with-state',
            to: href,
            state: {
              showSearchContext: true,
            } satisfies SearchContextHeaderLocationState,
          }}
          onClick={() => trackClick?.()}
        >
          <SearchHitHoverEffect />

          <SearchHitImageHolder $hiddenOnMobile={!hit.logo}>
            <SearchHitImage
              isImported={false}
              imageUrl={null}
              image={null}
              source={null}
              name={hit.name}
              logo={hit.logo}
            />
          </SearchHitImageHolder>

          <SearchGroupHitInfo
            hiring={hit.hiring}
            listingStats={listingStats}
            orgStat={orgStat}
            name={hit.name}
            orgType={hit.orgType}
            showLabel={showLabel}
            pillItems={[]}
          />
        </SearchHitInteractiveContentWrapper>

        {searchSavedListingsStore && (
          <SearchHitFloatingSaveButton
            hit={hit}
            searchSavedListingsStore={searchSavedListingsStore}
          />
        )}
      </SearchHitCard>
    </SearchHitHolder>
  );
}
